import React from 'react';

import styled from '@emotion/styled';

import { DoorToDoor__BundleEnum } from '@graphql/platform';

import { useTrack } from '@root/initializers/wt';

import { formatCurrency } from '@utils/formatters';

import { useChallengerPackingAmount } from '../hooks/use_challenger_packing_amount';
import {
  CardPadded,
  HelpText,
  Question,
  SectionStack,
  SectionTitle,
  SelectRow,
  SelectStack,
} from '../shared';
import { CHALLENGER as T } from '../tokens';
import { DisposalSection } from './disposal';

/** Figma 707:10131 — packing card + disposal card, 12px gap. */
const PackingDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[12]};
  width: 100%;
`;

/** Figma 6:748 — question + help, then 24px to options. */
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${T.space[24]};
`;

type Props = {
  value?: DoorToDoor__BundleEnum;
  onChange: (bundle: DoorToDoor__BundleEnum) => void;
  disposalSelected?: boolean;
  onDisposalChange: (selected: boolean | undefined) => void;
};

export const PackingSection: React.FC<Props> = ({
  value,
  onChange,
  disposalSelected,
  onDisposalChange,
}) => {
  const track = useTrack();
  // Packing $ by plan size + ZIP (not quote-gated pricingSummary).
  const { amount: packingPrice, loading: packingPriceLoading } =
    useChallengerPackingAmount();

  const selectBundle = (bundle: DoorToDoor__BundleEnum) => {
    onChange(bundle);
    // Match classic bundles.tsx WT objectName for package selection.
    track({
      value: bundle,
      label: 'Pickup package',
      objectName: 'package_radio_card',
    });
  };

  return (
    <SectionStack>
      <SectionTitle>Packing and disposal</SectionTitle>
      <PackingDetails>
        <CardPadded>
          <Header>
            <Question>Do you want help packing boxes?</Question>
            <HelpText>
              Don’t have time to pack your items into boxes? Our team is here to
              help you.
            </HelpText>
          </Header>

          <SelectStack>
            <SelectRow
              selected={value === DoorToDoor__BundleEnum.Basic}
              onClick={() => selectBundle(DoorToDoor__BundleEnum.Basic)}
              title="No, I’ll pack before Clutter arrives"
              description="You pack your items into boxes."
              price="Included"
            />
            <SelectRow
              selected={value === DoorToDoor__BundleEnum.PickupAndPacking}
              onClick={() =>
                selectBundle(DoorToDoor__BundleEnum.PickupAndPacking)
              }
              title="Yes, Clutter packs during my pickup"
              description="We bring boxes and packing materials and pack your items into boxes."
              price={
                packingPriceLoading || packingPrice == null
                  ? '—'
                  : formatCurrency(packingPrice, { maximumFractionDigits: 0 })
              }
            />
          </SelectStack>
        </CardPadded>

        <DisposalSection value={disposalSelected} onChange={onDisposalChange} />
      </PackingDetails>
    </SectionStack>
  );
};
